import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatINR } from "@/lib/apiClient";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { Button } from "@/components/ui/button";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  FileText, ArrowLeft, Trash2, ArrowDownLeft, ArrowUpRight, Wallet,
  Lightbulb, TrendingUp, UploadCloud, Download, AlertTriangle, ArrowUp,
} from "lucide-react";
import { toast } from "sonner";

const COLORS = ["#6366F1", "#0F172A", "#10B981", "#F59E0B", "#38BDF8", "#8B5CF6", "#F472B6", "#94A3B8"];

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso.length <= 10 ? iso + "T00:00:00" : iso);
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

const rs = (n) => "Rs. " + new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Number(n || 0));

function downloadReportPdf(r) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const M = 40;
  let y = 50;
  doc.setFont("helvetica", "bold").setFontSize(22).setTextColor(15, 23, 42);
  doc.text("Invesy", M, y);
  doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(120, 120, 120);
  doc.text("Investments made Easy", M, y + 13);
  doc.setFontSize(9).setTextColor(150, 150, 150);
  doc.text("Spending report", 555, y, { align: "right" });

  y += 42;
  doc.setFont("helvetica", "bold").setFontSize(15).setTextColor(15, 23, 42);
  doc.text(String(r.filename || "Statement"), M, y);
  doc.setFont("helvetica", "normal").setFontSize(10).setTextColor(90, 90, 90);
  y += 16;
  doc.text(`${fmtDate(r.period?.from)} - ${fmtDate(r.period?.to)}   |   ${r.transaction_count} transactions`, M, y);

  autoTable(doc, {
    startY: y + 14,
    theme: "grid",
    head: [["Money in", "Money out", "Net", "Invested"]],
    body: [[rs(r.total_in), rs(r.total_out), rs(r.net), rs(r.investments_total)]],
    headStyles: { fillColor: [15, 23, 42], halign: "center" },
    bodyStyles: { halign: "center", fontStyle: "bold" },
    styles: { fontSize: 10, cellPadding: 6 },
    margin: { left: M, right: M },
  });

  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 18,
    head: [["Where your money went", "Amount", "% of spend"]],
    body: (r.by_category || []).map((c) => [c.category, rs(c.amount), `${c.pct}%`]),
    headStyles: { fillColor: [99, 102, 241] },
    columnStyles: { 1: { halign: "right" }, 2: { halign: "right" } },
    styles: { fontSize: 10, cellPadding: 6 },
    margin: { left: M, right: M },
  });

  let cy = doc.lastAutoTable.finalY + 24;
  if (r.summary) {
    doc.setFont("helvetica", "bold").setFontSize(11).setTextColor(15, 23, 42);
    doc.text("Summary", M, cy);
    cy += 15;
    doc.setFont("helvetica", "normal").setFontSize(10).setTextColor(70, 70, 70);
    const lines = doc.splitTextToSize(r.summary, 515);
    doc.text(lines, M, cy);
    cy += lines.length * 13 + 10;
  }
  if (r.insights?.length) {
    doc.setFont("helvetica", "bold").setFontSize(11).setTextColor(15, 23, 42);
    doc.text("Insights", M, cy);
    cy += 15;
    doc.setFont("helvetica", "normal").setFontSize(10).setTextColor(70, 70, 70);
    r.insights.forEach((t) => {
      const lines = doc.splitTextToSize(`- ${t}`, 515);
      doc.text(lines, M, cy);
      cy += lines.length * 13 + 3;
    });
  }

  if (r.transactions?.length) {
    autoTable(doc, {
      startY: cy + 8,
      head: [["Date", "Description", "Category", "Type", "Amount"]],
      body: r.transactions.map((t) => [
        fmtDate(t.date), t.description, t.category, t.type,
        (t.type === "income" ? "+" : "-") + rs(t.amount).replace("Rs. ", ""),
      ]),
      headStyles: { fillColor: [15, 23, 42] },
      columnStyles: { 4: { halign: "right" } },
      styles: { fontSize: 9, cellPadding: 5 },
      margin: { left: M, right: M },
    });
    cy = doc.lastAutoTable.finalY;
  }

  doc.setFontSize(8).setTextColor(160, 160, 160);
  doc.text("Educational only. No investment is guaranteed. Generated by Invesy.", M, Math.min(cy + 24, 800));

  const safe = String(r.filename || "statement").replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "");
  doc.save(`Invesy-Report-${safe}.pdf`);
}

function Stat({ icon: Icon, label, value, tone }) {
  const t = { in: "text-emerald-600 bg-emerald-50", out: "text-red-500 bg-red-50", net: "text-slate-900 bg-slate-100", inv: "text-indigo-600 bg-indigo-50" };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className={`h-8 w-8 rounded-xl flex items-center justify-center mb-2 ${t[tone]}`}><Icon size={16} /></div>
      <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">{label}</p>
      <p className="font-mono font-bold text-lg text-slate-900 mt-0.5">{value}</p>
    </div>
  );
}

export default function Reports() {
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sp, setSp] = useSearchParams();

  const loadList = useCallback(async () => {
    try {
      const { data } = await api.get("/reports");
      setList(data);
    } finally {
      setLoading(false);
    }
  }, []);

  const open = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/reports/${id}`);
      setSelected(data);
    } catch {
      toast.error("Report not found");
    }
  }, []);

  useEffect(() => {
    loadList();
    const id = sp.get("id");
    if (id) open(id);
  }, [loadList, open, sp]);

  const remove = async (id) => {
    await api.delete(`/reports/${id}`);
    setList((l) => l.filter((r) => r.id !== id));
    if (selected?.id === id) { setSelected(null); setSp({}); }
    toast.success("Report deleted");
  };

  const back = () => { setSelected(null); setSp({}); };

  // ---------- Detail ----------
  if (selected) {
    const r = selected;
    return (
      <div className="p-6 md:p-10 max-w-4xl mx-auto">
        <button onClick={back} data-testid="report-back" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-900 mb-5 transition-colors duration-200">
          <ArrowLeft size={16} /> All reports
        </button>

        <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-500 mb-1">Statement report</p>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-slate-900 break-all">{r.filename}</h1>
            <p className="text-sm text-slate-400 mt-1">{fmtDate(r.period?.from)} – {fmtDate(r.period?.to)} · {r.transaction_count} transactions</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => downloadReportPdf(r)} data-testid="report-download" className="rounded-full gap-2">
              <Download size={16} /> Download PDF
            </Button>
            <Button variant="outline" onClick={() => remove(r.id)} data-testid="report-delete" className="rounded-full gap-2 text-red-500 border-red-200 hover:bg-red-50">
              <Trash2 size={16} /> Delete
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <Stat icon={ArrowDownLeft} label="Money in" value={formatINR(r.total_in)} tone="in" />
          <Stat icon={ArrowUpRight} label="Money out" value={formatINR(r.total_out)} tone="out" />
          <Stat icon={Wallet} label="Net" value={formatINR(r.net)} tone="net" />
          <Stat icon={TrendingUp} label="Invested" value={formatINR(r.investments_total)} tone="inv" />
        </div>

        {r.overspend_flags?.length > 0 && (
          <div className="rounded-3xl border border-amber-200 bg-amber-50 p-5 mb-6" data-testid="overspend-flags">
            <p className="font-heading font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <AlertTriangle size={18} className="text-amber-600" /> Spending that jumped this time
            </p>
            <div className="flex flex-wrap gap-2">
              {r.overspend_flags.map((f) => (
                <div key={f.category} data-testid="overspend-flag"
                  className="flex items-center gap-2 rounded-2xl bg-white border border-amber-200 px-3.5 py-2">
                  <span className="text-sm font-medium text-slate-800">{f.category}</span>
                  <span className="text-xs font-bold text-amber-700 flex items-center gap-0.5">
                    <ArrowUp size={12} /> {f.increase_pct}%
                  </span>
                  <span className="text-xs text-slate-400">vs usual {formatINR(f.avg)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-6 mb-6">
          <div className="rounded-3xl border border-slate-200 bg-white p-6 soft-shadow">
            <h3 className="font-heading font-semibold text-lg mb-4">Where your money went</h3>
            {r.by_category.length === 0 ? (
              <p className="text-sm text-slate-400 h-40 flex items-center justify-center">No expenses in this statement.</p>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={r.by_category} dataKey="amount" nameKey="category" innerRadius={50} outerRadius={85} paddingAngle={2}>
                      {r.by_category.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => formatINR(v)} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-2 mt-2">
                  {r.by_category.map((c, i) => (
                    <div key={c.category} className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2 text-slate-600">
                        <span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                        {c.category}
                        {c.flag && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-amber-700 bg-amber-100 rounded-full px-1.5 py-0.5">
                            <ArrowUp size={10} /> {c.increase_pct}%
                          </span>
                        )}
                      </span>
                      <span className="font-mono text-slate-900">{formatINR(c.amount)} <span className="text-slate-400">· {c.pct}%</span></span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-6 soft-shadow">
            <h3 className="font-heading font-semibold text-lg mb-3">Summary</h3>
            <p className="text-sm text-slate-600 leading-relaxed mb-5" data-testid="report-summary">{r.summary}</p>
            {r.insights?.length > 0 && (
              <div className="space-y-2.5 border-t border-slate-100 pt-4">
                {r.insights.map((t, i) => (
                  <div key={i} className="flex gap-2.5 text-sm text-slate-600"><Lightbulb size={17} className="text-indigo-500 shrink-0 mt-0.5" /><span>{t}</span></div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white divide-y divide-slate-100 overflow-hidden soft-shadow">
          {(r.transactions || []).map((t) => (
            <div key={t.id} className="flex items-center gap-4 px-5 py-3">
              <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${t.type === "income" ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>
                {t.type === "income" ? <ArrowDownLeft size={16} /> : <ArrowUpRight size={16} />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-slate-900 truncate">{t.description}</p>
                <p className="text-xs text-slate-400">{t.category} · {fmtDate(t.date)}</p>
              </div>
              <p className={`font-mono font-semibold ${t.type === "income" ? "text-emerald-600" : "text-slate-900"}`}>{t.type === "income" ? "+" : "−"}{formatINR(t.amount)}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ---------- List ----------
  return (
    <div className="p-6 md:p-10 max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-slate-900">Reports</h1>
        <p className="text-slate-500 mt-1">Spending reports from your uploaded statements.</p>
      </div>

      {loading ? (
        <div className="h-40 flex items-center justify-center text-slate-400">Loading…</div>
      ) : list.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-slate-300 bg-white p-12 text-center soft-shadow">
          <div className="h-14 w-14 rounded-2xl bg-indigo-50 flex items-center justify-center mx-auto mb-4"><FileText size={26} className="text-indigo-600" /></div>
          <p className="font-heading font-semibold text-lg text-slate-900">No reports yet</p>
          <p className="text-sm text-slate-500 mt-1.5 max-w-sm mx-auto">Upload a bank or UPI statement and Invesy will study it and generate a spending report here.</p>
          <Link to="/app/activity"><Button className="rounded-full bg-slate-900 hover:bg-slate-800 gap-2 mt-5"><UploadCloud size={17} /> Upload a statement</Button></Link>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {list.map((r, i) => (
            <motion.button
              key={r.id}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              onClick={() => open(r.id)}
              data-testid="report-card"
              className="text-left rounded-3xl border border-slate-200 bg-white p-5 soft-shadow hover:border-indigo-300 transition-colors duration-200"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-2xl bg-slate-100 flex items-center justify-center shrink-0"><FileText size={18} className="text-slate-600" /></div>
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900 truncate">{r.filename}</p>
                  <p className="text-xs text-slate-400">{fmtDate(r.created_at)} · {r.transaction_count} txns</p>
                </div>
              </div>
              <div className="flex items-baseline justify-between">
                <span className="text-sm text-slate-400">Spent</span>
                <span className="font-mono font-bold text-xl text-slate-900">{formatINR(r.total_out)}</span>
              </div>
              {r.by_category?.[0] && (
                <p className="text-xs text-slate-400 mt-1">Top: {r.by_category[0].category} ({r.by_category[0].pct}%)</p>
              )}
            </motion.button>
          ))}
        </div>
      )}
    </div>
  );
}
